import { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../Context';
import { AuthContextApi } from '../../Context/api';
import {
    CalendarIcon,
    InformationCircleIcon,
    CubeIcon
} from '@heroicons/react/24/outline'
import { toast } from 'react-toastify';
import CardMetricas from '../../Components/Cards/Metricas';
import Header from '../../Components/Header';
import FormBusca from '../../Components/Forms/Busca';
import { colorMap } from '../../Common';
import ServicosTable from '../../Components/Table/Servicos';
import ModalServico from '../../Components/Modal/Servicos';
import ModalVisualizacaoServico from './ModalVisualizacao';
import { jsonConfig } from '../../Config';

const Servicos = () => {

    const { states, setters } = useContext(AuthContext)
    const {
        checkLogin,
        getServicos,
        postServico,
        patchServico,
        deleteServico
    } = useContext(AuthContextApi)

    const navigate = useNavigate()
    const [query, setQuery] = useState('')
    const tableScrollRef = useRef(null)
    const tableContainerRef = useRef(null)

    const [showLeftArrow, setShowLeftArrow] = useState(false)
    const [showRightArrow, setShowRightArrow] = useState(true)
    const [arrowTop, setArrowTop] = useState('50%')

    const [searchTerm, setSearchTerm] = useState('')
    const [selectedFilterField, setSelectedFilterField] = useState('nome')
    const [selectedSortField, setSelectedSortField] = useState('data_criacao')
    const [sortDirection, setSortDirection] = useState('desc')
    const [recordsPerPage, setRecordsPerPage] = useState(10)

    const [modalVisualizacao, setModalVisualizacao] = useState(false)
    const [servicoSelecionado, setServicoSelecionado] = useState(null)

    const filterOptions = [
        { value: 'nome', label: 'Nome' },
        { value: 'descricao', label: 'Descrição' },
        { value: 'codigo_servico', label: 'Código do Serviço' }
    ]

    const sortOptions = [
        { value: 'nome', label: 'Nome' },
        { value: 'municipal', label: 'Municipal' },
        { value: 'codigo_servico', label: 'Código do Serviço' },
        { value: 'data_criacao', label: 'Data de Criação' }
    ]

    const primeiroLoad = async () => {
        const params = new URLSearchParams({
            page: 0,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection,
            filter: selectedFilterField,
            search: ''
        })
        await checkLogin()
        await getServicos(params.toString())
    }

    const loadData = async () => {
        const params = new URLSearchParams({
            page: states.page || 0,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection,
            filter: selectedFilterField,
            search: searchTerm
        })
        await getServicos(params.toString())
    }

    const actionModalServicoEdit = async (id, data) => {
        const response = await patchServico(id, data)
        if (response) {
            setters.setModalServico(false)
            primeiroLoad()
        }
    }

    const actionModalServicoCreate = async (data) => {
        const response = await postServico(data)
        if (response) {
            setters.setModalServico(false)
            primeiroLoad()
        }
    }

    const deleteServicoAction = async (servico) => {
        setters.setModalMsg(true)
        setters.setMsgModalMsg('Excluir Serviço')
        setters.setDescModalMsg(`Tem certeza que deseja excluir o serviço "${servico.nome}"? Esta ação não pode ser desfeita.`)
        setters.setPerigoModalMsg(true)
        setters.setTitleButtonModalMsg('Excluir')
        setters.setFunctionModalMsg(() => async () => {
            setters.setModalMsg(false)
            const resp = await deleteServico(servico.id)
            if (resp) {
                primeiroLoad()
            }
            setters.setModalMsg(false)
        })
    }

    const next = async () => {
        setters.setPage(states.page + 1)
    }

    const previous = async () => {
        if (states.page > 0) {
            setters.setPage(states.page - 1)
        }
    }

    const search = async (data) => {
        const params = new URLSearchParams({
            page: states.page || 0,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection,
            filter: selectedFilterField,
            search: searchTerm
        })
        await getServicos(params.toString())
    }

    const clear = async () => {
        setters.setPage(0)
        const params = new URLSearchParams({
            page: 0,
            per_page: 10,
            sort: 'data_criacao',
            sort_dir: 'desc',
            filter: 'nome',
            search: ''
        })
        await getServicos(params.toString())
    }

    const color = 'blue'
    const colorClasses = colorMap[color] || colorMap.blue

    const userPermission = localStorage.getItem('permisssion')
    const isAdmin = userPermission === jsonConfig.roles.admin

    const addServico = () => {
        setters.setIsEdit(false)
        setters.setServico({})
        setters.setModalServico(true)
    }

    const editServico = (servico) => {
        setters.setIsEdit(true)
        setters.setServico(servico)
        setters.setModalServico(true)
    }

    const viewServico = (servico) => {
        setServicoSelecionado(servico)
        setModalVisualizacao(true)
    }

    const checkScrollPosition = () => {
        if (tableScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current
            const isAtStart = scrollLeft <= 1
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1
            setShowLeftArrow(!isAtStart)
            setShowRightArrow(!isAtEnd)
        }
    }

    const calculateArrowPosition = () => {
        if (tableContainerRef.current && tableScrollRef.current) {
            const containerRect = tableContainerRef.current.getBoundingClientRect()
            const scrollRect = tableScrollRef.current.getBoundingClientRect()
            const headerHeight = scrollRect.top - containerRect.top
            const tableHeight = scrollRect.height
            const topPosition = headerHeight + (tableHeight / 2)
            const containerHeight = containerRect.height
            const percentage = (topPosition / containerHeight) * 100
            setArrowTop(`${percentage}%`)
        }
    }

    const headers = [
        {
            label: 'Ações',
            icon: CubeIcon,
            key: 'actions'
        },
        {
            label: 'Nome',
            icon: InformationCircleIcon,
            key: 'nome'
        },
        {
            label: 'Municipal',
            icon: InformationCircleIcon,
            key: 'municipal'
        },
        {
            label: 'Código',
            icon: InformationCircleIcon,
            key: 'codigo_servico'
        },
        {
            label: 'Criação',
            icon: CalendarIcon,
            key: 'data_criacao'
        }
    ]

    useEffect(() => {
        const tableElement = tableScrollRef.current
        if (tableElement) {
            const timer = setTimeout(() => {
                checkScrollPosition()
                calculateArrowPosition()
            }, 1)

            tableElement.addEventListener('scroll', checkScrollPosition)
            window.addEventListener('resize', () => {
                checkScrollPosition()
                calculateArrowPosition()
            })

            return () => {
                clearTimeout(timer)
                tableElement.removeEventListener('scroll', checkScrollPosition)
                window.removeEventListener('resize', checkScrollPosition)
            }
        }
    }, [states.dataServicos, states.loading])

    useEffect(() => {
        primeiroLoad()

        return () => {
            setQuery('')
            setters.setDataServicos([])
            setters.setServico({})
            setters.setModalServico(false)
        }
    }, [])

    useEffect(() => {
        if (states.dataServicos.length > 0) {
            search()
        }
    }, [states.page])

    return (
        <>
            <Header
                title="Serviços"
                description="Gerencie os serviços do sistema"
                icon={CubeIcon}
                buttonText="Adicionar Serviço"
                buttonAction={addServico}
                hasAction={true}
                hasReturn={false}
                color='blue'
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {isAdmin && <CardMetricas dataMetricas={null} color={color} />}
                <FormBusca
                    filterOptions={filterOptions}
                    sortOptions={sortOptions}
                    search={search}
                    handleClear={clear}
                    color={color}
                    items={{
                        searchTerm,
                        setSearchTerm,
                        selectedFilterField,
                        setSelectedFilterField,
                        selectedSortField,
                        setSelectedSortField,
                        sortDirection,
                        setSortDirection,
                        recordsPerPage,
                        setRecordsPerPage,
                    }}
                    haveData={states.dataServicos.length > 0}
                />

                <ServicosTable
                    data={states.dataServicos}
                    loading={states.loading}
                    title="Serviços"
                    headers={headers}
                    viewServico={viewServico}
                    editServico={editServico}
                    deleteServico={deleteServicoAction}
                    color={color}
                    previous={previous}
                    next={next}
                    page={states.page}
                    isAdmin={isAdmin}
                />
            </div>

            <ModalServico
                states={states}
                setters={setters}
                actionCreate={actionModalServicoCreate}
                actionEdit={actionModalServicoEdit}
                color={color}
            />

            <ModalVisualizacaoServico
                isOpen={modalVisualizacao}
                onClose={() => setModalVisualizacao(false)}
                servico={servicoSelecionado}
                color={color}
            />
        </>
    )
}

export default Servicos;

